import { useContext, useMemo } from "react"
import { useAppSelector } from "@/reducers/hooks"
import {
  ScheduledWorkflow,
  selectAllWorkflows,
} from "@/reducers/openclawWorkflowsSlice"
import { XmppContext } from "@/stream/xmppClient"
import {
  ChatAutomationScope,
  ChatWorkflows,
  workflowsForChat,
} from "@/lib/chatAutomations"

export type RoomWorkflows = ChatWorkflows & {
  /** Rows on screen. The badge reads this. */
  count: number
  /** Mine to manage — the agent rejects a write from anyone else. */
  isMine: (job: ScheduledWorkflow) => boolean
}

/**
 * What one driver room's automations tab shows: the rules about this driver,
 * the fleet-wide rules that cover this truck along with every other, the
 * reminders for this truck, and the transport brief. Rules aimed at somebody
 * else's truck are gone.
 */
export const useRoomWorkflows = (scope: ChatAutomationScope): RoomWorkflows => {
  const { myJid } = useContext(XmppContext)
  const storedWorkflows = useAppSelector(selectAllWorkflows)
  const myJidStr = `${myJid.local}@${myJid.getDomain()}`

  return useMemo(() => {
    const workflows = workflowsForChat(storedWorkflows, scope)
    return {
      ...workflows,
      // The transport brief is always on the list, so it is always in the
      // badge — count what the room shows.
      count:
        workflows.forThisDriver.length +
        workflows.fleetWide.length +
        workflows.fleetReminders.length +
        1,
      isMine: (job: ScheduledWorkflow) =>
        job.scope === "local" && job.author === myJidStr,
    }
  }, [storedWorkflows, scope, myJidStr])
}
